import type { ReactNode } from "react";
import { StatGrid, StatTile } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkTone } from "@NP-Webber/crm-kit/dist/next/index.js";

export type ListStatCard = {
  key: string | null;
  label: string;
  value: number | string;
  /** legacy colour — mapped to the closest tone when `tone` is not given */
  color?: string;
  tone?: CkTone;
  /** crm-kit/next icon name */
  iconName?: string;
  /** legacy icon node (ignored by the new design — use iconName) */
  icon?: ReactNode;
  sub?: ReactNode;
};

export type ListStatStripProps = {
  title?: string;
  cards: ListStatCard[];
  activeKey: string | null;
  onSelect: (key: string | null) => void;
  columns?: number;
  loading?: boolean;
};

function toneOf(color: string | undefined): CkTone {
  const c = String(color || "").toLowerCase();
  const m = c.match(/^#([0-9a-f]{6})$/);
  if (!m) return "primary";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  if (r > 180 && g < 120) return r > 220 && g > 90 ? "warning" : "danger";
  if (r > 200 && g > 90 && b < 80) return "warning";
  if (g > r && g > b) return "success";
  if (b > r && b > g) return r > 100 ? "violet" : "primary";
  return "neutral";
}

const DEFAULT_ICON: Record<CkTone, string> = {
  primary: "list",
  success: "check",
  warning: "pause",
  danger: "alert",
  info: "info",
  violet: "sparkle",
  neutral: "list",
};

/** Clickable stat tiles that toggle a client-side filter key (new-design StatGrid). */
export function ListStatStrip({ cards, activeKey, onSelect, loading = false }: ListStatStripProps) {
  return (
    <div className="list-stat-strip">
      <StatGrid min={170}>
        {cards.map((card, index) => {
          const selected = activeKey === card.key;
          const tone = card.tone ?? toneOf(card.color);
          return (
            <StatTile
              key={`${card.key ?? "__all__"}-${index}`}
              label={card.label}
              value={card.value}
              sub={card.sub}
              icon={card.iconName ?? DEFAULT_ICON[tone]}
              tone={tone}
              loading={loading}
              active={selected}
              onClick={() => onSelect(selected ? null : card.key)}
            />
          );
        })}
      </StatGrid>
    </div>
  );
}
